/**
 * Build-time configuration, read once and normalised once.
 *
 * Every value here is inlined by Vite at build time — there is no runtime
 * config endpoint and no `window.__ENV__`. Changing one of these means
 * rebuilding the image, which is exactly what `clinic-panel/docker/Dockerfile`
 * does with its `--build-arg` list.
 */

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function positiveInt(value: string | undefined, fallback: number): number {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const env = {
  /**
   * API origin **without** the version segment — `https://bookora.net/api` in
   * production, `/api` in local development (Vite proxies it).
   *
   * The `/v1` is appended by the axios mutator, in exactly one place. Baking it
   * in here instead produces `…/api/v1/v1/patients` the first time somebody
   * writes a URL that already carries it.
   */
  apiBaseUrl: trimTrailingSlash(import.meta.env.VITE_API_BASE_URL ?? '/api'),

  /** Public marketing site, for the "صفحهٔ عمومی کلینیک" link. */
  siteUrl: trimTrailingSlash(import.meta.env.VITE_SITE_URL ?? 'https://bookora.net'),

  /** Patient PWA origin, for links handed to a patient. */
  patientAppUrl: trimTrailingSlash(
    import.meta.env.VITE_PATIENT_APP_URL ?? 'https://my.bookora.net',
  ),

  appName: import.meta.env.VITE_APP_NAME ?? 'بوکورا',

  tokenStorageKey: import.meta.env.VITE_TOKEN_STORAGE_KEY ?? 'bookora_clinic_auth',

  themeStorageKey: 'bookora_clinic_theme',

  /** Neshan web-map key for the clinic address picker. Empty disables the map. */
  neshanMapKey: import.meta.env.VITE_NESHAN_MAP_KEY ?? '',

  /**
   * Minutes of inactivity before the idle warning appears. A clinic panel sits
   * open on a reception desk in a room patients walk through, so this is a
   * privacy control, not a convenience one.
   */
  idleMinutes: positiveInt(import.meta.env.VITE_SESSION_IDLE_MINUTES, 30),

  /** Seconds the user has to answer the idle warning before being signed out. */
  idleGraceSeconds: 60,

  /**
   * Page-traffic tracking (self-hosted Umami, same-origin `/u/*`). `"1"` turns
   * it on; anything else, including unset, leaves it off and no tracker is
   * ever inserted. The website id is not configuration: it is the committed
   * `UMAMI_WEBSITES.clinic.id` in `@bookora/shared/analytics`. deploy.sh turns
   * the flag on only once the remote nginx serves `/u/*`.
   */
  analytics: import.meta.env.VITE_ANALYTICS === '1',

  isDev: import.meta.env.DEV,
} as const;

export type Env = typeof env;
